import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError, gbp } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

function todayISO() {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
}

export default function ManagerDashboard() {
  const [employees, setEmployees] = useState([]);
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({ hours_worked: 0, total_wage: 0, deliveries_done: 0, delivery_amount: 0 });
  const [date, setDate] = useState(todayISO());
  const [form, setForm] = useState({
    employee_id: "", hours_worked: "", hourly_rate: "",
    deliveries_done: "0", delivery_amount: "0",
  });
  const [submitting, setSubmitting] = useState(false);

  const loadEmployees = useCallback(async () => {
    try {
      const { data } = await api.get("/employees");
      setEmployees(data.filter((e) => e.active !== false));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }, []);

  const loadEntries = useCallback(async () => {
    try {
      const { data } = await api.get("/entries", { params: { start_date: date, end_date: date } });
      setEntries(data.entries);
      setTotals(data.totals);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  }, [date]);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const onEmployeeChange = (employee_id) => {
    const emp = employees.find((e) => e.id === employee_id);
    setForm((f) => ({
      ...f,
      employee_id,
      hourly_rate: emp?.default_hourly_rate ? String(emp.default_hourly_rate) : f.hourly_rate,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) { toast.error("Pick an employee"); return; }
    setSubmitting(true);
    try {
      await api.post("/entries", {
        employee_id: form.employee_id,
        date,
        hours_worked: parseFloat(form.hours_worked || "0"),
        hourly_rate: parseFloat(form.hourly_rate || "0"),
        deliveries_done: parseInt(form.deliveries_done || "0", 10),
        delivery_amount: parseFloat(form.delivery_amount || "0"),
      });
      toast.success("Entry logged");
      setForm({ employee_id: "", hours_worked: "", hourly_rate: "", deliveries_done: "0", delivery_amount: "0" });
      loadEntries();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Deleted");
      loadEntries();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const computedTotal = useMemo(() => {
    const h = parseFloat(form.hours_worked || "0");
    const r = parseFloat(form.hourly_rate || "0");
    return Number.isFinite(h * r) ? h * r : 0;
  }, [form.hours_worked, form.hourly_rate]);

  return (
    <div className="p-6 md:p-8 max-w-[1400px]">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label-uc">Daily Labour Entry</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Today's shift log</h1>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="date" className="label-uc">Date</Label>
          <Input
            id="date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded-none border-zinc-300 w-44 font-mono-data"
            data-testid="entry-date-filter"
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-1 bg-white border border-zinc-200 p-5" data-testid="entry-form">
          <div className="label-uc mb-3">Add entry</div>

          <div className="space-y-3">
            <div>
              <Label className="label-uc">Employee</Label>
              <Select value={form.employee_id} onValueChange={onEmployeeChange}>
                <SelectTrigger className="rounded-none border-zinc-300 mt-1.5" data-testid="entry-employee-select">
                  <SelectValue placeholder={employees.length ? "Select employee" : "No employees yet — add some in Employees"} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-uc">Hours</Label>
                <Input type="number" step="0.25" min="0" value={form.hours_worked}
                  onChange={(e) => setForm({ ...form, hours_worked: e.target.value })}
                  className="rounded-none border-zinc-300 mt-1.5 font-mono-data" data-testid="entry-hours-input" required />
              </div>
              <div>
                <Label className="label-uc">Rate £/hr</Label>
                <Input type="number" step="0.01" min="0" value={form.hourly_rate}
                  onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                  className="rounded-none border-zinc-300 mt-1.5 font-mono-data" data-testid="entry-rate-input" required />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-uc">Deliveries</Label>
                <Input type="number" step="1" min="0" value={form.deliveries_done}
                  onChange={(e) => setForm({ ...form, deliveries_done: e.target.value })}
                  className="rounded-none border-zinc-300 mt-1.5 font-mono-data" data-testid="entry-deliveries-input" />
              </div>
              <div>
                <Label className="label-uc">Delivery £</Label>
                <Input type="number" step="0.01" min="0" value={form.delivery_amount}
                  onChange={(e) => setForm({ ...form, delivery_amount: e.target.value })}
                  className="rounded-none border-zinc-300 mt-1.5 font-mono-data" data-testid="entry-delivery-amount-input" />
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-zinc-200 pt-3">
              <div>
                <div className="label-uc">Wage</div>
                <div className="font-mono-data text-lg font-semibold mt-1" data-testid="entry-computed-wage">{gbp(computedTotal)}</div>
              </div>
              <Button type="submit" disabled={submitting} className="rounded-none bg-zinc-950 hover:bg-zinc-800 h-11 px-6" data-testid="entry-submit-button">
                <Plus className="w-4 h-4 mr-2" />{submitting ? "Saving…" : "Log entry"}
              </Button>
            </div>
          </div>
        </form>

        {/* Today's entries */}
        <div className="lg:col-span-2 bg-white border border-zinc-200">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <div className="label-uc">Entries for {date}</div>
              <div className="font-display text-xl font-bold mt-0.5" data-testid="entries-count">{entries.length} entries</div>
            </div>
            <div className="flex gap-6 text-right">
              <div>
                <div className="label-uc">Total Hrs</div>
                <div className="font-mono-data font-semibold" data-testid="totals-hours">{totals.hours_worked}</div>
              </div>
              <div>
                <div className="label-uc">Wages</div>
                <div className="font-mono-data font-semibold" data-testid="totals-wage">{gbp(totals.total_wage)}</div>
              </div>
              <div>
                <div className="label-uc">Deliv.</div>
                <div className="font-mono-data font-semibold" data-testid="totals-deliveries">{totals.deliveries_done}</div>
              </div>
              <div>
                <div className="label-uc">Deliv. £</div>
                <div className="font-mono-data font-semibold" data-testid="totals-delivery-amount">{gbp(totals.delivery_amount)}</div>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto" data-testid="entries-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left">
                  <th className="px-3 py-2 label-uc">Employee</th>
                  <th className="px-3 py-2 label-uc text-right">Hrs</th>
                  <th className="px-3 py-2 label-uc text-right">Rate</th>
                  <th className="px-3 py-2 label-uc text-right">Wage</th>
                  <th className="px-3 py-2 label-uc text-right">Deliv.</th>
                  <th className="px-3 py-2 label-uc text-right">Deliv. £</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-12 text-center text-zinc-400">No entries for this date.</td></tr>
                )}
                {entries.map((e) => (
                  <tr key={e.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`entry-row-${e.id}`}>
                    <td className="px-3 py-2">{e.employee_name}</td>
                    <td className="px-3 py-2 text-right font-mono-data">{e.hours_worked}</td>
                    <td className="px-3 py-2 text-right font-mono-data">{gbp(e.hourly_rate)}</td>
                    <td className="px-3 py-2 text-right font-mono-data font-semibold">{gbp(e.total_wage)}</td>
                    <td className="px-3 py-2 text-right font-mono-data">{e.deliveries_done}</td>
                    <td className="px-3 py-2 text-right font-mono-data">{gbp(e.delivery_amount)}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => remove(e.id)} className="text-zinc-400 hover:text-[#DF1A22]" data-testid={`entry-delete-${e.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
